import { useQuery } from "@rocicorp/zero/react"
import { cx } from "cva"
import * as React from "react"
import { type Components, default as ReactMarkdown } from "react-markdown"
import type { Row } from "../../generated/schema.ts"
import * as queries from "../../shared/queries.ts"
import type { OneOf } from "../../shared/types.ts"
import * as Auth from "../lib/auth.ts"
import { allowedHTMLElements, remarkPlugins } from "../lib/markdown.ts"
import * as Shiki from "../lib/shiki.ts"

export const Markdown = React.memo(Markdown_)
function Markdown_(props: Markdown.Props) {
  const { content, repositoryId, repositoryOwner } = props

  return (
    <ReactMarkdown
      allowedElements={allowedHTMLElements}
      components={React.useMemo(
        () => ({
          ...components,
          span(props) {
            const { node, ...rest } = props
            if (
              "data-github-reference" in rest &&
              "data-github-type" in rest &&
              typeof rest["data-github-reference"] === "number" &&
              typeof rest["data-github-type"] === "string"
            )
              return (
                <GitHubReference
                  expanded={
                    "data-expanded" in rest &&
                    typeof rest["data-expanded"] === "boolean"
                      ? rest["data-expanded"]
                      : false
                  }
                  number={rest["data-github-reference"]}
                  owner={
                    "data-github-owner" in rest
                      ? (rest["data-github-owner"] as never)
                      : undefined
                  }
                  repo={
                    "data-github-repo" in rest
                      ? (rest["data-github-repo"] as never)
                      : undefined
                  }
                  repositoryId={repositoryId}
                  repositoryOwner={repositoryOwner}
                  type={rest["data-github-type"] as never}
                />
              )
            return <span {...rest} />
          },
        }),
        [repositoryId, repositoryOwner],
      )}
      remarkPlugins={remarkPlugins}
    >
      {content}
    </ReactMarkdown>
  )
}

export declare namespace Markdown {
  type Props = {
    content: string
    repositoryId: string
    repositoryOwner: Pick<Row.account | Row.organization, "id" | "login">
  }
}

const components = {
  a(props) {
    const { node, ...rest } = props
    return (
      <a
        className="text-[#0969da] underline-offset-2 hover:underline dark:text-[#4493f8]"
        rel="noreferrer"
        target="_blank"
        {...rest}
      />
    )
  },
  blockquote(props) {
    const { node, ...rest } = props
    return (
      <blockquote
        className="my-1 border-neutral-6 border-s-[4px] ps-3 text-muted dark:border-neutral-7"
        {...rest}
      />
    )
  },
  em(props) {
    const { node, ...rest } = props
    return <em className="font-italic" {...rest} />
  },
  code(props) {
    const { node, ...rest } = props

    const parsed = React.useMemo(() => {
      if (typeof props.children !== "string") return
      const match = props.children.match(/(.+)\{:([\w-]+)\}$/)
      if (!match) return
      const code = match[1] ?? props.children
      const lang = match.at(2)
      if (!code || !lang) return
      return { code, lang }
    }, [props.children])

    if (parsed)
      return (
        <InlineCode
          className="shiki-inline"
          code={parsed.code}
          lang={parsed.lang}
        />
      )

    return <code className="shiki-inline" {...rest} />
  },
  ol(props) {
    const { node, ...rest } = props
    return <ol className="list-decimal ps-4" {...rest} />
  },
  pre(props) {
    const { children, node, ...rest } = props

    const parsed = React.useMemo(() => {
      const [firstChild] = node?.children ?? []
      if (
        firstChild &&
        firstChild.type === "element" &&
        firstChild.tagName === "code" &&
        firstChild.children[0]?.type === "text"
      ) {
        const { className } = firstChild.properties
        const [, lang = "plaintext"] =
          /language-(\w+)/.exec(String(className) || "") ?? []
        return {
          code: firstChild.children[0].value,
          lang,
          meta: firstChild.data?.meta,
        }
      }
      return undefined
    }, [node?.children])

    if (parsed)
      return (
        <CodeBlock
          className="my-1"
          code={parsed.code}
          lang={parsed.lang}
          meta={parsed.meta}
        />
      )

    return (
      <pre className="shiki my-1 px-4" {...rest}>
        {children}
      </pre>
    )
  },
  strong(props) {
    const { node, ...rest } = props
    return <strong className="font-bold" {...rest} />
  },
  ul(props) {
    const { node, ...rest } = props
    return <ul className="list-disc ps-4" {...rest} />
  },
} satisfies Components

function CodeBlock(props: {
  className?: string | undefined
  code: string
  lang: string
  meta: string | null | undefined
}) {
  const { className, code, lang, meta } = props

  const [__html, setHTML] = React.useState<string | undefined>(undefined)
  React.useEffect(() => {
    // TODO: Move shiki to server?
    Shiki.codeToHtml({ code, lang, meta }).then(setHTML).catch(console.error)
  }, [code, lang, meta])

  const [copied, setCopied] = React.useState(false)
  const copyToClipboard = React.useCallback(() => {
    if (copied) return

    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2_000)
  }, [code, copied])

  const fallbackChildren = React.useMemo(() => {
    const lines = code.trim().split("\n")
    return (
      <pre className="shiki">
        <code
          {...Shiki.metaToLineNumbersProperties(meta, {
            lineCount: lines.length,
            style: "object",
          })}
        >
          {lines.map((line, index) => (
            <span
              className="line"
              // biome-ignore lint/suspicious/noArrayIndexKey: prevent dupes
              key={`${line}${index}`}
            >
              {line}
            </span>
          ))}
        </code>
      </pre>
    )
  }, [code, meta])

  return (
    <div
      className={`group/code-block relative flex items-start${className ? ` ${className}` : ""}`}
    >
      <div
        className="w-full flex-grow"
        {...(__html
          ? { dangerouslySetInnerHTML: { __html } }
          : { children: fallbackChildren })}
      />

      <button
        className="absolute end-0 m-3 hidden size-7 items-center justify-center rounded-md border border-default bg-muted focus:flex group-hover/code-block:flex"
        onClick={copyToClipboard}
        title="Copy Code"
        type="button"
      >
        {copied ? (
          <OcticonCheck16 className="size-4 text-green-3" />
        ) : (
          <OcticonCopy16 className="size-3.5 text-neutral-9" />
        )}
      </button>
    </div>
  )
}

function InlineCode(props: {
  className?: string | undefined
  code: string
  lang: string
}) {
  const { className, code, lang } = props

  const [__html, setHTML] = React.useState<string | undefined>(undefined)
  React.useEffect(() => {
    Shiki.codeToHast({ code, lang })
      .then((fragment) => {
        const parent = fragment.children.at(0)
        if (parent?.type === "element" && parent.tagName === "pre") {
          // convert parent to span element
          parent.tagName = "code"
          // strip layers of nesting and add children directly to parent
          const container = parent.children.at(0)
          if (container?.type === "element") {
            const content = container.children.at(0)
            if (content?.type === "element") parent.children = content.children
          }
        }
        setHTML(Shiki.codeHastToHtml(fragment))
      })
      .catch(console.error)
  }, [code, lang])

  return (
    <span
      className={className}
      {...(__html
        ? { dangerouslySetInnerHTML: { __html } }
        : { children: code })}
    />
  )
}

function GitHubReference(
  props: {
    expanded: boolean
    number: number
    repositoryId: string
    repositoryOwner: Pick<Row.account | Row.organization, "id" | "login">
  } & OneOf<
    | { owner: string; repo: string; type: "issues" | "pull" }
    | { type: "unknown" }
  >,
) {
  const {
    expanded,
    number,
    owner,
    repo,
    repositoryId,
    repositoryOwner: ownerAttrs,
    type,
  } = props

  const { data: authData } = Auth.useAuthData()
  const [repository, repositoryState] = useQuery(
    queries.repositoryWithGitHubReference(authData, {
      number,
      owner,
      repo,
      repositoryId,
      type,
    }),
    { ttl: "5m" },
  )

  const repositoryOwner = React.useMemo(() => {
    if (!repository) return
    if (repository.account) return repository.account
    if (repository.organization) return repository.organization
    return
  }, [repository])

  const fullNameWithNumber = `${
    (
      repositoryOwner
        ? repositoryOwner.id !== ownerAttrs.id
        : owner && ownerAttrs.login !== owner
    )
      ? `${owner}/`
      : ""
  }${repository && repository.id === repositoryId ? "" : repo}#${number}`
  const githubHref = `https://github.com/${repositoryOwner?.login ?? owner}/${repository?.name ?? repo}/${type === "unknown" ? (repository?.pullRequests ? "pull" : "issues") : type}/${number}`

  if (
    repositoryState.type === "complete" &&
    !repository?.issues &&
    !repository?.pullRequests
  ) {
    if (type === "unknown") return <span>#{number}</span>
    return (
      <a
        className="text-[#0969da] underline underline-offset-2 dark:text-[#4493f8]"
        href={githubHref}
        rel="noreferrer"
        target="_blank"
      >
        {fullNameWithNumber}
      </a>
    )
  }

  return (
    <span className="ms-0.5 leading-[22px]">
      <IconGitHubReference
        className="me-0.75 inline size-4 overflow-visible align-text-bottom"
        issue={repository?.issues}
        pullRequest={repository?.pullRequests}
      />
      <a
        className="text-[#0969da] underline underline-offset-2 dark:text-[#4493f8]"
        href={githubHref}
        rel="noreferrer"
        target="_blank"
      >
        {repository && expanded && (
          <>
            <span className="font-semibold">
              {repository.issues
                ? repository.issues.title
                : repository.pullRequests?.title}
            </span>{" "}
          </>
        )}
        <span className={expanded ? "text-muted" : undefined}>
          {fullNameWithNumber}
        </span>
      </a>
    </span>
  )
}

function IconGitHubReference(props: {
  className?: string | undefined
  issue: Row.issue | undefined
  pullRequest: Row.pull_request | undefined
}) {
  const { className, issue, pullRequest } = props

  if (issue)
    switch (issue.state) {
      case "closed": {
        if (issue.state_reason === "not_planned")
          return <OcticonSkip16 className={cx("text-muted", className)} />
        return (
          <OcticonIssueClosed16 className={cx("text-purple-4", className)} />
        )
      }
      case "open":
        return (
          <OcticonIssueOpened16 className={cx("text-green-3", className)} />
        )
    }

  if (pullRequest)
    switch (pullRequest.state) {
      case "closed": {
        if (pullRequest.merged_at)
          return (
            <OcticonGitMerge16 className={cx("text-purple-4", className)} />
          )
        return (
          <OcticonGitPullRequestClosed16
            className={cx("text-red-4", className)}
          />
        )
      }
      case "open": {
        if (pullRequest.draft)
          return (
            <OcticonGitPullRequestDraft16
              className={cx("text-muted", className)}
            />
          )
        return (
          <OcticonGitPullRequest16 className={cx("text-green-3", className)} />
        )
      }
    }

  return null
}
